'use client';

import classNames from 'classnames';
import { getCookie, setCookie } from 'cookies-next';
import { AnimatePresence, m } from 'framer-motion';
import { useEffect, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './message-banner.module.scss';

export interface MessageBannerProps {
  /** Indicates the banner type */
  bannerType: Content.Components['MessageBannerType'];
  /** The class name to apply */
  className?: string;
  /** Indicates whether to display the banner */
  enabled: Content.Components['MessageBannerDto']['enable'];
  /** The headline for the message banner */
  headline: Content.Components['MessageBannerDto']['headline'];
  /** The hash value of the last published date */
  lastPublishedDateHash: Content.Components['MessageBannerDto']['lastPublishedDateHash'];
  /** The text for the message banner */
  text: Content.Components['MessageBannerDto']['text'];
}

const variants: Record<Content.Components['MessageBannerType'], string> = {
  information: styles.isInformation,
  success: styles.isSuccess,
  warning: styles.isWarning,
};

const getIconName = (
  bannerType: Content.Components['MessageBannerType'],
): IconName => {
  switch (bannerType) {
    case 'information':
      return 'Info';

    case 'success':
      return 'CheckMark';

    case 'warning':
      return 'Warning';

    default:
      return 'Info';
  }
};

const MessageBanner = ({
  bannerType = 'information',
  className,
  enabled,
  headline,
  lastPublishedDateHash,
  text,
}: MessageBannerProps) => {
  const { t } = useI18n();
  const [renderMessageBanner, setRenderMessageBanner] = useState(false);

  useEffect(() => {
    const cookieRender = getCookie(lastPublishedDateHash);
    const isCookieRenderTrue = cookieRender === 'true';

    if (cookieRender === undefined || isCookieRenderTrue) {
      setCookie(lastPublishedDateHash, 'true');
      setRenderMessageBanner(true);
    } else if (cookieRender === 'false') {
      setRenderMessageBanner(false);
    }
  }, [lastPublishedDateHash]);

  const handleDismissMessageBanner = () => {
    setCookie(lastPublishedDateHash, 'false');
    setRenderMessageBanner(false);
  };

  if (!enabled) return;

  return (
    <AnimatePresence>
      {renderMessageBanner && (
        <div className={classNames(styles.component, className)}>
          <m.div
            animate="show"
            className={classNames(styles.banner, variants[bannerType])}
            exit="hidden"
            initial="hidden"
            transition={{ damping: 20, stiffness: 300, type: 'spring' }}
            variants={{
              hidden: { opacity: 0, y: 50 },
              show: { opacity: 1, y: 0 },
            }}
          >
            <div className={styles.iconContainer}>
              <div className={styles.iconWrapper}>
                <Icon name={getIconName(bannerType)} size={24} />
              </div>
            </div>

            <div className={styles.textContainer}>
              <Typography tagStyle="bodyLargeBold">{headline}</Typography>

              {text && <RichText text={text} />}
            </div>

            <div className={styles.buttonContainer}>
              <button
                aria-label={t('items.common.words.close')}
                className={styles.icon}
                onClick={handleDismissMessageBanner}
              >
                <Icon name="Close" size={24} />
              </button>
            </div>
          </m.div>
        </div>
      )}
    </AnimatePresence>
  );
};

MessageBanner.displayName = 'MessageBanner';

export default MessageBanner;
